const doc = document;

// ADR-0520: structured page bootstrap belongs in <script type="application/json">
// tags parsed here. Keep data-* attributes for simple scalar hooks only.
type UnknownRecord = Record<string, unknown>;

export function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null;
}

export function isNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

export function isString(value: unknown): value is string {
  return typeof value === "string";
}

export function isArray<T>(value: unknown, guard?: (item: unknown) => item is T): value is T[] {
  if (!Array.isArray(value)) {
    return false;
  }
  if (!guard) {
    return true;
  }
  return value.every((item) => guard(item));
}

export function parseJSONScript<T>(
  id: string,
  fallback: T,
  guard?: (value: unknown) => value is T
): T {
  const node = doc.getElementById(id);
  if (!(node instanceof HTMLScriptElement)) {
    return fallback;
  }
  try {
    const raw = String(node.textContent || "").trim();
    if (!raw) {
      return fallback;
    }
    let parsed: unknown = JSON.parse(raw);
    // Some templates serialize JSON as a JSON string (double-encoded).
    // Parse up to two additional layers so callers always receive structured data.
    for (let depth = 0; depth < 2; depth += 1) {
      if (typeof parsed !== "string") {
        break;
      }
      const nestedRaw = parsed.trim();
      if (!nestedRaw) {
        break;
      }
      if (!(nestedRaw.startsWith("{") || nestedRaw.startsWith("[") || nestedRaw.startsWith("\""))) {
        break;
      }
      try {
        parsed = JSON.parse(nestedRaw);
      } catch (_err) {
        break;
      }
    }
    if (guard) {
      return guard(parsed) ? parsed : fallback;
    }
    return parsed as T;
  } catch (_err) {
    return fallback;
  }
}

export function closestFromEvent(event: Event, selector: string): HTMLElement | null {
  const target = event.target;
  if (target instanceof HTMLElement) {
    return target.closest(selector);
  }
  if (target instanceof Node && target.nodeType === Node.TEXT_NODE) {
    const textTarget = target as Text;
    if (textTarget.parentElement instanceof HTMLElement) {
      return textTarget.parentElement.closest(selector);
    }
  }
  return null;
}
